import React, { useState, useEffect } from 'react';
import { SmartCaptcha } from '@yandex/smart-captcha';
import './css/ApplicationPopup.css';

const ApplicationPopup = ({ isOpen, type = "none", title = "Оставить заявку", onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    consent: true // Добавлено поле для согласия
  });

  const [formErrors, setFormErrors] = useState({
    phone: '',
    consent: '' // Добавлено поле для ошибки согласия
  });

  const [utmData, setUtmData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [isCaptchaCompleted, setIsCaptchaCompleted] = useState(false);

  // Блокируем скролл при открытии попапа
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  // Сбор UTM-меток
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['source', 'medium', 'campaign', 'term', 'content'].forEach(key => {
      const value = params.get(`utm_${key}`);
      if (value) utm[`utm_${key}`] = value;
    });
    setUtmData(utm);
  }, []);

  // Валидация телефона
  const validatePhone = (phone) => {
    if (!phone) return '';

    const cleanedPhone = phone.replace(/[^\d+]/g, '');

    const russianPhoneRegex = /^(\+7|7|8)?[\s\-]?\(?[489][0-9]{2}\)?[\s\-]?[0-9]{3}[\s\-]?[0-9]{2}[\s\-]?[0-9]{2}$/;
    const internationalPhoneRegex = /^\+[1-9]\d{1,14}$/;

    if (!russianPhoneRegex.test(phone) && !internationalPhoneRegex.test(cleanedPhone)) {
      return 'Введите корректный номер телефона';
    }

    return '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      
      // Очищаем ошибку согласия при изменении
      if (checked && formErrors.consent) {
        setFormErrors(prev => ({
          ...prev,
          consent: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (name === 'phone') {
      const error = validatePhone(value);
      setFormErrors(prev => ({
        ...prev,
        phone: error
      }));
    }

    if (submitStatus === 'error') {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const handleCaptchaSuccess = (token) => {
    setCaptchaToken(token);
    setIsCaptchaCompleted(true);
  };

  const handleCaptchaError = () => {
    setCaptchaToken('');
    setIsCaptchaCompleted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      setFormErrors(prev => ({ ...prev, phone: phoneError }));
      return;
    }

    // Валидация согласия
    if (!formData.consent) {
      setFormErrors(prev => ({ ...prev, consent: 'Необходимо согласие на обработку персональных данных' }));
      return;
    }

    if (!captchaToken) {
      setSubmitStatus('error');
      setErrorMessage('Пожалуйста, подтвердите, что вы не робот.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('/api/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, ...utmData, type, smart_token: captchaToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Ошибка ${response.status}`);
      }

      const result = await response.json();
      console.log('Успешно отправлено:', result);
      setSubmitStatus('success');

    } catch (error) {
      console.error('Ошибка отправки:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Не удалось отправить заявку. Попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting || formErrors.phone || !isCaptchaCompleted || !formData.consent;

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-container">
        <div className="popup-content" onClick={(e) => e.stopPropagation()}>
          <button className="popup-close" onClick={onClose} aria-label="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {submitStatus !== 'success' ? (
            <>
              <div className="popup-header">
                <div className="popup-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 className="popup-title">{title}</h2>
                <p className="popup-subtitle">
                  Заполните форму, и наш специалист свяжется с вами в течение 15 минут
                </p>
              </div>

              <form className="popup-form" onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <div className="input-container">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="popup-form-input"
                        placeholder=" "
                        required
                        disabled={isSubmitting}
                      />
                      <label className="input-label">Имя *</label>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-container">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="popup-form-input"
                        placeholder=" "
                        disabled={isSubmitting}
                      />
                      <label className="input-label">Компания</label>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-container">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="popup-form-input"
                        placeholder=" "
                        required
                        disabled={isSubmitting}
                      />
                      <label className="input-label">Email *</label>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-container">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`popup-form-input ${formErrors.phone ? 'popup-form-input--error' : ''}`}
                        placeholder=" "
                        required
                        disabled={isSubmitting}
                      />
                      <label className="input-label">Телефон *</label>
                    </div>
                    {formErrors.phone && (
                      <div className="input-error">
                        <svg className="input-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {formErrors.phone}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container textarea-container">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === ' ') {
                          e.stopPropagation(); // Предотвращаем всплытие пробела
                        }
                      }}
                      className="popup-form-textarea"
                      placeholder=" "
                      rows="3"
                      disabled={isSubmitting}
                    />
                    <label className="input-label">Сообщение</label>
                  </div>
                </div>

                {/* Блок согласия */}
                <div className="form-group">
                  <div className={`consent-checkbox ${formErrors.consent ? 'consent-checkbox--error' : ''}`}>
                    <label className="consent-label">
                      <input
                        type="checkbox"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleInputChange}
                        
                        className="consent-input"
                        
                      />
                      <span className="consent-custom-checkbox">
                        <svg className="consent-checkmark" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="consent-text">
                        Я соглашаюсь на обработку моих персональных данных в соответствии с{' '}
                        <a href="/privacy-policy" className="form-link" target="_blank" rel="noopener noreferrer">
                          политикой конфиденциальности
                        </a>
                        *
                      </span>
                    </label>
                    {formErrors.consent && (
                      <div className="consent-error">
                        <svg className="consent-error-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {formErrors.consent}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <SmartCaptcha
                    sitekey="ysc1_vd0Wt2IZbBocsMfrImlMwv1bXl9kQXecjqmd4rCu19f3e19f"
                    onSuccess={handleCaptchaSuccess}
                    onError={handleCaptchaError}
                    hl="ru"
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="form-error">
                    <svg className="form-error-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {errorMessage}
                  </div>
                )}

                <div className="form-footer">
                  <button
                    type="submit"
                    className={`submit-button ${isSubmitting ? 'loading' : ''} ${isSubmitDisabled ? 'submit-button--disabled' : ''}`}
                    disabled={isSubmitDisabled}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="button-spinner"></div>
                        Отправка...
                      </>
                    ) : (
                      <>
                        Отправить заявку
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="success-message">
              <div className="success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="success-title">Заявка отправлена!</h3>
              <p className="success-text">
                Наш менеджер свяжется с вами в ближайшее время для уточнения деталей
              </p>
            </div>
          )}

          <div className="popup-contacts">
            <div className="contact-item">
              <div className="contact-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span>support@altoor.ru</span>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span>+7 (495) 902-03-02</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationPopup;